import React from "react";
import { observer } from "mobx-react-lite";
import ProductsListContainer from "../ProductsListContainer";
import { searchStore } from "features/ProductSearch/SearchStore";
import ProductRowsList, { RowsListProps } from "../ProductRowsList";
import EmptyState from "../EmptyState";
import { InternalProductSearchOutput } from "api-client";
import { useTranslation } from "react-i18next";
import { IconCopy, IconEdit, IconInfoCircle } from "@tabler/icons-react";
import { useNavigate } from "react-router";
import useFeatureFlag from "hooks/useFeatureFlag";
import { ROUTES_ENUM } from "routes/consts";
import ProductDetailsModal from "features/ProductDetailsModal";
import useComponentActions from "./useComponentActions";
import { ProductActionMode } from "utils";

const DefaultProducts = (props: RowsListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isFeatureVisible } = useFeatureFlag();
  const { saveStateAndGetPath } = useComponentActions();

  function shouldShowEmptyView() {
    return (
      !searchStore.productSearchLoading &&
      searchStore.searchResults.items.length === 0
    );
  }

  const duplicateProduct = (id?: string | null) => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: { id: id, compliant: false, mode: ProductActionMode.Duplicate },
    });
  };

  const editProduct = (id?: string | null) => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`, {
      state: { id: id, compliant: false, mode: ProductActionMode.Edit },
    });
  };

  const settingsItems = (
    product: InternalProductSearchOutput | null | undefined
  ) => {
    const itemList = [
      {
        name: t("mapping.duplicateProduct"),
        icon: <IconCopy width={18} className="mr-2" />,
        onClick: () => duplicateProduct(product?.id),
        className: "text-gray-700",
        disabled: false,
      },
      {
        name: t("mapping.showDetails"),
        icon: <IconInfoCircle width={18} className="mr-2" />,
        onClick: () =>
          searchStore.setOpenDetails({ open: true, product: product }),
        className: "text-gray-700",
        disabled: false,
      },
    ];

    if (isFeatureVisible("edit_default_components")) {
      itemList.push({
        name: t("mapping.editProduct"),
        icon: <IconEdit width={18} className="mr-2" />,
        onClick: () => editProduct(product?.id),
        className: "text-gray-700",
        disabled: false,
      });
    }
    return itemList;
  };

  return (
    <>
      <ProductsListContainer>
        {shouldShowEmptyView() ? (
          <EmptyState />
        ) : (
          <ProductRowsList match={props.match} settingsItems={settingsItems} />
        )}
      </ProductsListContainer>
      <ProductDetailsModal
        productId={searchStore.openDetails.product?.id}
        productName={searchStore.openDetails.product?.name}
        productType={searchStore.openDetails.product?.type}
        open={searchStore.openDetails.open}
        close={() =>
          searchStore.setOpenDetails({ open: false, product: undefined })
        }
      />
    </>
  );
};

export default observer(DefaultProducts);
